/**
 * MiniPlayer Component - REDESIGNED
 *
 * A sleek, floating mini audio player with premium aesthetics.
 * Features smooth animations, interactive progress slider,
 * and refined controls with haptic-like feedback.
 *
 * Design Philosophy:
 * - Floating island design with subtle shadow depth
 * - Warm gold accents on cream background
 * - Full user control over playback position
 * - Smooth spring animations for all interactions
 */

import { type FC, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { goldTintDisc } from '@/styles/tones';
import { useLanguage } from '@/hooks/useLanguage';
import { Slider } from '@/components/ui';
import {
  useAudioPlayerStore,
  selectIsPlayerVisible,
  selectHasNextTrack,
  selectHasPreviousTrack,
} from '@/stores/audioPlayerStore';
import { getAudioTitle, formatDuration } from '@/types/content';

// =============================================================================
// Types
// =============================================================================

export interface MiniPlayerProps {
  /** Additional CSS classes */
  className?: string;
  /** Bottom offset class for positioning above tab bars (e.g., "bottom-20") */
  bottomOffset?: string;
}

// =============================================================================
// Singleton Audio Element
// =============================================================================

let globalAudioElement: HTMLAudioElement | null = null;

function getAudioElement(): HTMLAudioElement {
  if (!globalAudioElement) {
    globalAudioElement = new Audio();
    globalAudioElement.preload = 'metadata';
  }
  return globalAudioElement;
}

/**
 * Destroy the global audio element and release resources.
 * Should be called on logout to prevent memory leaks.
 */
export function destroyGlobalAudioElement(): void {
  if (globalAudioElement) {
    globalAudioElement.pause();
    globalAudioElement.src = '';
    globalAudioElement = null;
  }
}

// =============================================================================
// Animation Variants
// =============================================================================

const playerVariants = {
  hidden: {
    y: 120,
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 35,
      mass: 1,
    },
  },
  exit: {
    y: 80,
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 1, 1] as const,
    },
  },
};

const buttonVariants = {
  rest: { scale: 1 },
  hover: { scale: 1.08 },
  pressed: { scale: 0.92 },
};

// =============================================================================
// Sound Bars Mini Visualizer
// =============================================================================

const SoundBarsMini: FC<{ isPlaying: boolean }> = ({ isPlaying }) => {
  return (
    <div className="flex items-end gap-0.5 h-4">
      {[6, 10, 8, 12, 7].map((maxHeight, i) => (
        <motion.div
          key={i}
          className="w-[3px] bg-gold-hero rounded-full"
          animate={
            isPlaying
              ? {
                  height: [maxHeight * 0.3, maxHeight, maxHeight * 0.5],
                }
              : { height: maxHeight * 0.4 }
          }
          transition={
            isPlaying
              ? {
                  duration: 0.4 + i * 0.08,
                  repeat: Infinity,
                  repeatType: 'reverse' as const,
                  ease: 'easeInOut',
                }
              : { duration: 0.2 }
          }
        />
      ))}
    </div>
  );
};

// =============================================================================
// MiniPlayer Component
// =============================================================================

export const MiniPlayer: FC<MiniPlayerProps> = ({ className, bottomOffset }) => {
  const { isArabic, direction, t } = useLanguage();

  // Store state and actions
  const currentTrack = useAudioPlayerStore((state) => state.currentTrack);
  const isPlaying = useAudioPlayerStore((state) => state.isPlaying);
  const currentTime = useAudioPlayerStore((state) => state.currentTime);
  const duration = useAudioPlayerStore((state) => state.duration);

  const isVisible = useAudioPlayerStore(selectIsPlayerVisible);
  const hasNext = useAudioPlayerStore(selectHasNextTrack);
  const hasPrevious = useAudioPlayerStore(selectHasPreviousTrack);

  const pause = useAudioPlayerStore((state) => state.pause);
  const resume = useAudioPlayerStore((state) => state.resume);
  const next = useAudioPlayerStore((state) => state.next);
  const previous = useAudioPlayerStore((state) => state.previous);
  const seek = useAudioPlayerStore((state) => state.seek);
  const close = useAudioPlayerStore((state) => state.close);
  const setCurrentTime = useAudioPlayerStore((state) => state.setCurrentTime);
  const setDuration = useAudioPlayerStore((state) => state.setDuration);

  const lastUrlRef = useRef<string | null>(null);

  // ==========================================================================
  // Audio Element Management
  // ==========================================================================

  useEffect(() => {
    const audio = getAudioElement();

    if (currentTrack && audio.src !== currentTrack.url) {
      if (lastUrlRef.current !== currentTrack.url) {
        audio.src = currentTrack.url;
        audio.load();
        lastUrlRef.current = currentTrack.url;
      }
    }

    if (currentTrack && isPlaying) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          pause();
        });
      }
    } else {
      audio.pause();
    }
  }, [currentTrack, isPlaying, pause]);

  // ==========================================================================
  // Audio Event Listeners
  // ==========================================================================

  useEffect(() => {
    const audio = getAudioElement();

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleDurationChange = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      const state = useAudioPlayerStore.getState();
      const currentIndex = state.playlist.findIndex(
        (t) => t.id === state.currentTrack?.id
      );
      if (currentIndex < state.playlist.length - 1) {
        next();
      } else {
        pause();
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [setCurrentTime, setDuration, next, pause]);

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      resume();
    }
  }, [isPlaying, pause, resume]);

  const handleClose = useCallback(() => {
    const audio = getAudioElement();
    audio.pause();
    audio.src = '';
    lastUrlRef.current = null;
    close();
  }, [close]);

  const handlePrevious = useCallback(() => {
    previous();
  }, [previous]);

  const handleNext = useCallback(() => {
    next();
  }, [next]);

  const handleSeek = useCallback(
    (value: number) => {
      const audio = getAudioElement();
      audio.currentTime = value;
      seek(value);
    },
    [seek]
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  const trackDuration = duration || currentTrack?.durationSeconds || 0;
  const displayCurrentTime = formatDuration(Math.floor(currentTime));
  const displayDuration = formatDuration(Math.floor(trackDuration));

  return (
    <AnimatePresence>
      {isVisible && currentTrack && (
        <motion.div
          key="mini-player"
          variants={playerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={cn(
            'fixed inset-x-0 z-40',
            bottomOffset || 'bottom-0',
            className
          )}
          dir={direction}
          role="region"
          aria-label={t('miniPlayer.nowPlaying')}
          data-testid="mini-player"
        >
          {/* Main player container */}
          <div
            className={cn(
              'mx-4 mb-4 md:mx-auto md:max-w-lg',
              'relative overflow-hidden',
              'bg-white/95 backdrop-blur-xl',
              'rounded-2xl',
              'border border-surface-200/60',
              'shadow-soft-xl'
            )}
          >
            {/* Progress slider at top - FULL USER CONTROL */}
            <div className="px-4 pt-3">
              <Slider
                value={currentTime}
                min={0}
                max={trackDuration || 100}
                step={1}
                onChange={handleSeek}
                aria-label={t('audio.currentTime')}
                className="h-2"
              />
            </div>

            {/* Player content */}
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Track info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  {isPlaying && <SoundBarsMini isPlaying={isPlaying} />}
                  <span className={cn('text-xs font-bold px-2 py-0.5 rounded-md', goldTintDisc)}>
                    {t('content.page', { number: currentTrack.pageNumber })}
                  </span>
                </div>
                <h4
                  className="font-bold text-ink-deep truncate text-sm"
                  data-testid="track-title"
                >
                  {getAudioTitle(currentTrack, isArabic)}
                </h4>
                <div className="flex items-center gap-1.5 mt-0.5 text-xs text-surface-500 tabular-nums">
                  <span className="force-ltr">{displayCurrentTime}</span>
                  <span className="text-surface-300">/</span>
                  <span className="force-ltr">{displayDuration}</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-1 shrink-0">
                {/* Previous button */}
                <motion.button
                  type="button"
                  onClick={handlePrevious}
                  disabled={!hasPrevious && currentTime < 3}
                  aria-label={t('miniPlayer.previous')}
                  className={cn(
                    'p-2 rounded-xl',
                    'flex items-center justify-center',
                    'text-surface-500 hover:text-ink-deep',
                    'hover:bg-surface-100 active:bg-surface-200',
                    'disabled:opacity-30 disabled:cursor-not-allowed',
                    'transition-colors duration-150'
                  )}
                  data-testid="previous-button"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="pressed"
                >
                  <SkipBack
                    size={20}
                    fill="currentColor"
                    style={{ transform: direction === 'rtl' ? 'scaleX(-1)' : undefined }}
                  />
                </motion.button>

                {/* Play/Pause button - Gold hero */}
                <motion.button
                  type="button"
                  onClick={handleTogglePlay}
                  aria-label={isPlaying ? t('miniPlayer.pause') : t('miniPlayer.play')}
                  className={cn(
                    'p-3 rounded-full',
                    'flex items-center justify-center',
                    'bg-gradient-to-br from-gold-hero to-gold-warm',
                    'text-ink-deep',
                    'shadow-gold-glow',
                    'transition-shadow duration-200',
                    'hover:shadow-gold-glow-lg'
                  )}
                  data-testid="play-pause-button"
                  variants={buttonVariants}
                  initial="rest"
                  whileHover="hover"
                  whileTap="pressed"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isPlaying ? (
                      <motion.div
                        key="pause"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.5, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Pause size={22} fill="currentColor" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="play"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.5, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Play size={22} fill="currentColor" className="ms-0.5" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>

                {/* Next button */}
                <motion.button
                  type="button"
                  onClick={handleNext}
                  disabled={!hasNext}
                  aria-label={t('miniPlayer.next')}
                  className={cn(
                    'p-2 rounded-xl',
                    'flex items-center justify-center',
                    'text-surface-500 hover:text-ink-deep',
                    'hover:bg-surface-100 active:bg-surface-200',
                    'disabled:opacity-30 disabled:cursor-not-allowed',
                    'transition-colors duration-150'
                  )}
                  data-testid="next-button"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="pressed"
                >
                  <SkipForward
                    size={20}
                    fill="currentColor"
                    style={{ transform: direction === 'rtl' ? 'scaleX(-1)' : undefined }}
                  />
                </motion.button>

                {/* Divider */}
                <div className="w-px h-6 bg-surface-200 mx-1" />

                {/* Close button */}
                <motion.button
                  type="button"
                  onClick={handleClose}
                  aria-label={t('miniPlayer.close')}
                  className={cn(
                    'p-2 rounded-xl',
                    'flex items-center justify-center',
                    'text-surface-600 hover:text-surface-800',
                    'hover:bg-surface-100 active:bg-surface-200',
                    'transition-colors duration-150'
                  )}
                  data-testid="close-button"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="pressed"
                >
                  <X size={20} />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MiniPlayer;
